<template>
  <div>
    <v-dialog v-if="mode === 'dialog'" v-model="uploadDialog" max-width="640px">
      <v-card>
        <v-card-title>PDF Transaktion importieren</v-card-title>
        <v-card-text>
          <v-alert
            v-if="error"
            type="error"
            variant="tonal"
            class="mb-3"
          >
            {{ error }}
          </v-alert>

          <v-file-input
            label="PDF"
            accept=".pdf,application/pdf"
            prepend-icon="mdi-file-pdf-box"
            variant="filled"
            density="compact"
            @change="onFileSelected"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeUploadDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            :disabled="!pdfFile"
            @click="submitDryRun"
          >
            PDF lesen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="mode === 'dialog'"
      v-model="resultDialog"
      max-width="820px"
    >
      <v-card>
        <v-card-title>PDF Import Ergebnis</v-card-title>
        <v-card-text>
          <v-alert v-if="error" type="error" variant="tonal" class="mb-3">{{ error }}</v-alert>
          <v-alert
            v-if="successMessage"
            type="success"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            {{ successMessage }}
            <div v-for="(line, index) in successDetails" :key="line" class="mt-1" :class="{ 'text-white font-weight-bold': index === 0 }">{{ line }}</div>
          </v-alert>

          <v-alert
            v-if="effectivePdfImportPostImportNotice"
            :type="effectivePdfImportPostImportNotice.type"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            <div class="font-weight-bold mb-1">{{ effectivePdfImportPostImportNotice.title }}</div>
            <div
              v-for="line in effectivePdfImportPostImportNotice.lines"
              :key="line"
            >
              {{ line }}
            </div>
          </v-alert>

          <v-alert
            v-if="pdfImportWarningLines.length"
            type="warning"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            <div class="font-weight-bold mb-1">Hinweis zur Buchung</div>
            <div
              v-for="(line, index) in pdfImportWarningLines"
              :key="`${index}-${line}`"
            >
              {{ line }}
            </div>
          </v-alert>

          <ImportBody v-if="!successMessage" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="resultDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <template v-if="mode === 'inline'">
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ error }}
      </v-alert>

      <v-alert
        v-if="successMessage"
        type="success"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ successMessage }}
        <div v-for="(line, index) in successDetails" :key="line" class="mt-1" :class="{ 'text-white font-weight-bold': index === 0 }">{{ line }}</div>
      </v-alert>

      <v-alert
        v-if="effectivePdfImportPostImportNotice"
        :type="effectivePdfImportPostImportNotice.type"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        <div class="font-weight-bold mb-1">{{ effectivePdfImportPostImportNotice.title }}</div>
        <div
          v-for="line in effectivePdfImportPostImportNotice.lines"
          :key="line"
        >
          {{ line }}
        </div>
      </v-alert>

      <v-alert
        v-if="pdfImportWarningLines.length"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        <div class="font-weight-bold mb-1">Hinweis zur Buchung</div>
        <div
          v-for="(line, index) in pdfImportWarningLines"
          :key="`${index}-${line}`"
        >
          {{ line }}
        </div>
      </v-alert>

      <ImportBody v-if="pdfImportResult" />

      <v-card-actions v-if="showInlineActions" class="shared-pdf-actions px-0">
        <slot name="inline-actions-start" />

        <v-spacer class="shared-pdf-action-spacer" />

        <v-btn
          class="shared-pdf-action-btn"
          variant="outlined"
          :loading="loading"
          :disabled="!pdfFile || loading"
          @click="submitDryRun"
        >
          PDF analysieren
        </v-btn>

        <v-btn
          class="shared-pdf-action-btn"
          color="primary"
          variant="flat"
          :loading="loading"
          :disabled="!canSubmitPdfImport"
          @click="submitFinal"
        >
          Transaktion buchen
        </v-btn>
      </v-card-actions>
    </template>
  </div>
</template>

<script>
import { h, resolveComponent } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import axios from '@/api/client'
import { normalizePdfFile } from '@/utils/pdfFile'

const STOCK_TYPE_OPTIONS = ['Aktie', 'ETF', 'Fonds', 'Rohstoff', 'Krypto']
const PDF_TRANSACTION_CATEGORY_OPTIONS = [
  { value: 1, title: 'Kauf' },
  { value: 2, title: 'Verkauf' },
  { value: 3, title: 'Dividende' },
  { value: 4, title: 'Steuer' },
  { value: 5, title: 'Cash Einzahlung' },
  { value: 6, title: 'Cash Auszahlung' }
]
const LAENDER = [
  { land: 'Argentinien', wert: 'Argentinien' },
  { land: 'Australien', wert: 'Australien' },
  { land: 'China', wert: 'China' },
  { land: 'Daenemark', wert: 'Daenemark' },
  { land: 'Deutschland', wert: 'Deutschland' },
  { land: 'Europa', wert: 'Europa' },
  { land: 'Frankreich', wert: 'Frankreich' },
  { land: 'Italien', wert: 'Italien' },
  { land: 'Japan', wert: 'Japan' },
  { land: 'Kanada', wert: 'Kanada' },
  { land: 'Niederlande', wert: 'Niederlande' },
  { land: 'Norwegen', wert: 'Norwegen' },
  { land: 'Schweden', wert: 'Schweden' },
  { land: 'Schweiz', wert: 'Schweiz' },
  { land: 'Suedkorea', wert: 'Suedkorea' },
  { land: 'Taiwan', wert: 'Taiwan' },
  { land: 'Uruguay', wert: 'Uruguay' },
  { land: 'USA', wert: 'USA' },
  { land: 'Welt', wert: 'Welt' }
]

export default {
  name: 'PdfTransactionImportDialog',

  components: {
    ImportBody: {
      name: 'PdfTransactionImportBody',
      inject: ['pdfImport'],
      render() {
        const ctx = this.pdfImport
        const VAlert = resolveComponent('v-alert')
        const VBtn = resolveComponent('v-btn')
        const VCard = resolveComponent('v-card')
        const VCardText = resolveComponent('v-card-text')
        const VCardTitle = resolveComponent('v-card-title')
        const VCol = resolveComponent('v-col')
        const VCombobox = resolveComponent('v-combobox')
        const VExpandTransition = resolveComponent('v-expand-transition')
        const VRow = resolveComponent('v-row')
        const VSelect = resolveComponent('v-select')
        const VSwitch = resolveComponent('v-switch')
        const VTextField = resolveComponent('v-text-field')

        const submitAction = h('div', { class: 'pdf-import-primary-action mb-3' }, [
          h(VBtn, {
            class: 'pdf-import-primary-action-btn',
            color: 'primary',
            variant: 'flat',
            loading: ctx.loading,
            disabled: !ctx.canSubmitPdfImport,
            onClick: ctx.submitFinal
          }, () => 'Transaktion buchen')
        ])

        const needsNewStock = ctx.showPdfImportCreateStockSection
        const createStockSection = needsNewStock
            ? h(VCard, { variant: 'tonal', class: 'pdf-import-section' }, {
              default: () => [
                h(VCardTitle, { class: 'text-subtitle-1' }, () => 'Neuer Stock'),
                h(VCardText, null, () => [
                  h(VAlert, { type: 'info', variant: 'tonal', density: 'compact', class: 'mb-3' }, () => ctx.pdfImportCreateStockPromptMessage),
                  h(VRow, { dense: true }, () => [
                    h(VCol, { cols: '12', md: '5' }, () => h(VTextField, { modelValue: ctx.pdfImportCreateStockForm.name, 'onUpdate:modelValue': v => { ctx.pdfImportCreateStockForm.name = v }, label: 'Neuer Stock Name', density: 'compact' })),
                    h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.pdfImportCreateStockForm.isin, 'onUpdate:modelValue': v => { ctx.pdfImportCreateStockForm.isin = v }, label: 'Neue Stock ISIN', density: 'compact' })),
                    h(VCol, { cols: '12', md: '3' }, () => h(VSelect, { modelValue: ctx.pdfImportCreateStockForm.typ, 'onUpdate:modelValue': v => { ctx.pdfImportCreateStockForm.typ = v }, items: ctx.stockTypeOptions, label: 'Typ', density: 'compact' })),
                    h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.pdfImportCreateStockForm.handelsplatz, 'onUpdate:modelValue': v => { ctx.pdfImportCreateStockForm.handelsplatz = v }, label: 'Handelsplatz', density: 'compact' })),
                    h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.pdfImportCreateStockForm.googleId, 'onUpdate:modelValue': v => { ctx.pdfImportCreateStockForm.googleId = v }, label: 'Google ID', density: 'compact' })),
                    h(VCol, { cols: '12', md: '4' }, () => h(VSelect, { modelValue: ctx.pdfImportCreateStockForm.land, 'onUpdate:modelValue': v => { ctx.pdfImportCreateStockForm.land = v }, items: ctx.laender, itemTitle: 'land', itemValue: 'wert', label: 'Land', density: 'compact', clearable: true }))
                  ]),
                  h('div', { class: 'pdf-import-create-actions' }, [
                    ctx.pdfImportCreateStockMessage
                      ? h(VAlert, { type: ctx.pdfImportCreateStockMessageType, variant: 'tonal', density: 'compact', class: 'pdf-import-create-message' }, () => ctx.pdfImportCreateStockMessage)
                      : null,
                    h(VBtn, { color: 'secondary', variant: 'flat', prependIcon: 'mdi-plus', loading: ctx.pdfImportCreateStockLoading, disabled: !ctx.canCreatePdfImportStock, onClick: ctx.createPdfImportStockFromPreview }, () => 'Stock anlegen und importieren')
                  ]),
                  ctx.showManualPdfImportStockCreation ? h('div', { class: 'mt-3' }, [
                    h('div', { class: 'text-caption mb-2' }, 'Manuelle Anlage: Name, ISIN, Typ, Handelsplatz, Google ID und Land vollständig ausfüllen. Eine Google-Sheets-Zeile wird dabei nicht automatisch angelegt.'),
                    h(VBtn, { color: 'primary', variant: 'flat', loading: ctx.pdfImportCreateStockLoading, disabled: !ctx.canCreateManualPdfImportStock, onClick: ctx.createManualPdfImportStock }, () => 'Stock mit diesen Angaben anlegen und importieren')
                  ]) : null
                ])
              ]
            })
            : null
        const taxOptimizationSection = ctx.isPdfImportTaxOptimization
          ? h(VCard, { variant: 'outlined', class: 'pdf-import-section pdf-import-tax-section' }, {
            default: () => [
              h(VCardTitle, { class: 'text-subtitle-1' }, () => 'Steuerliche Optimierung erkannt'),
              h(VCardText, null, () => [
                h(VAlert, { type: 'info', variant: 'tonal', density: 'compact', class: 'mb-3' }, () => 'Diese PDF wird automatisch als Steuer-Optimierung verbucht. Es wird kein neuer Stock angelegt.'),
                h('div', { class: 'pdf-import-kv' }, [
                  h('span', 'Kategorie'), h('strong', ctx.formatPdfCategory(ctx.pdfImportForm.category)),
                  h('span', 'Beschreibung'), h('strong', ctx.pdfImportTaxDescriptionText),
                  h('span', 'Datum'), h('strong', ctx.pdfImportForm.date || '-'),
                  h('span', 'Betrag'), h('strong', ctx.formatPdfSignedMoney(ctx.pdfImportTaxAmount)),
                  h('span', 'Cash-Stock'), h('strong', ctx.pdfImportTaxCashStockText),
                  h('span', 'Cash-Auswirkung'), h('strong', ctx.pdfImportTaxCashImpactText)
                ]),
                ctx.pdfImportTaxDetailRows.length
                  ? h('div', { class: 'pdf-import-tax-details mt-4' }, [
                    h('div', { class: 'text-caption mb-2' }, 'Steuerdetails'),
                    h('div', { class: 'pdf-import-kv' }, [
                      ...ctx.pdfImportTaxDetailRows.flatMap(row => [
                        h('span', row.label),
                        h('strong', ctx.formatPdfMoney(row.amount))
                      ]),
                      h('span', 'Gesamt'),
                      h('strong', ctx.formatPdfMoney(ctx.pdfImportTaxAmount))
                    ])
                  ])
                  : null
              ])
            ]
          })
          : null
        const transactionSummarySection = needsNewStock
          ? h(VCard, { variant: 'outlined', class: 'pdf-import-section' }, {
            default: () => [
              h(VCardTitle, { class: 'text-subtitle-1' }, () => 'Gelesene Transaktion'),
              h(VCardText, null, () => h('div', { class: 'pdf-import-kv' }, [
                h('span', 'Kategorie'), h('strong', ctx.formatPdfCategory(ctx.pdfImportForm.category)),
                h('span', 'Menge'), h('strong', String(ctx.pdfImportForm.amount || '-')),
                h('span', 'Preis pro Stueck'), h('strong', String(ctx.pdfImportForm.price || '-')),
                h('span', 'Betrag'), h('strong', ctx.pdfImportSettlementAmountText),
                h('span', 'Gebuehr + Steuer'), h('strong', ctx.pdfImportSettlementDeductionsText),
                h('span', 'Datum'), h('strong', ctx.pdfImportForm.date || '-')
              ]))
            ]
          })
          : null
        const confirmStockSection = !needsNewStock && !ctx.isPdfImportTaxOptimization
          ? h(VCard, { variant: 'outlined', class: 'pdf-import-section' }, {
            default: () => [
              h(VCardTitle, { class: 'text-subtitle-1' }, () => 'Bitte bestaetigen / ergaenzen'),
              h(VCardText, null, () => [
                ctx.pdfImportMissingFieldsText ? h(VAlert, { type: 'warning', variant: 'tonal', class: 'mb-3' }, () => `Fehlt noch: ${ctx.pdfImportMissingFieldsText}`) : null,
                ctx.pdfImportStockReactivationText ? h(VAlert, { type: 'info', variant: 'tonal', class: 'mb-3' }, () => ctx.pdfImportStockReactivationText) : null,
                ctx.pdfImportSellQuantityExceedsStock ? h(VAlert, { type: 'error', variant: 'tonal', class: 'mb-3' }, () => `Verkauf nicht moeglich: Verkaufsmenge ${ctx.pdfImportForm.amount} ist groesser als DB-Bestand ${ctx.selectedPdfImportStockQuantityText}.`) : null,
                h(VRow, { dense: true }, () => [
                  h(VCol, { cols: '12', md: '6' }, () => h(VCombobox, { modelValue: ctx.pdfImportForm.stocks_idstocks, 'onUpdate:modelValue': v => { ctx.pdfImportForm.stocks_idstocks = ctx.normalizePdfImportStockSelection(v) }, items: ctx.stockSelectItems, itemTitle: 'title', itemValue: 'value', returnObject: false, label: 'Stock bestaetigen', placeholder: ctx.pdfImportStockConfirmPlaceholder, density: 'compact', clearable: true, error: ctx.pdfImportMissingFields.includes('stocks_idstocks') && !ctx.hasConfirmedPdfImportStock })),
                  h(VCol, { cols: '12', md: '6' }, () => h(VTextField, { modelValue: ctx.pdfImportForm.isin, 'onUpdate:modelValue': v => { ctx.pdfImportForm.isin = v }, label: 'ISIN uebernehmen', density: 'compact' })),
                  h(VCol, { cols: '12', md: '6' }, () => h(VTextField, { modelValue: ctx.selectedPdfImportStockText, label: 'DB Stock', density: 'compact', readonly: true })),
                  h(VCol, { cols: '12', md: '4' }, () => h(VSelect, { modelValue: Number(ctx.pdfImportForm.category || 0), 'onUpdate:modelValue': v => { ctx.pdfImportForm.category = v }, items: ctx.pdfImportCategoryOptions, itemTitle: 'title', itemValue: 'value', label: 'Kategorie', density: 'compact' })),
                  h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.pdfImportForm.amount, 'onUpdate:modelValue': v => { ctx.pdfImportForm.amount = v }, label: 'Menge', density: 'compact' })),
                  h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.selectedPdfImportStockQuantityText, label: 'DB Bestand', density: 'compact', readonly: true })),
                  h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.pdfImportForm.price, 'onUpdate:modelValue': v => { ctx.pdfImportForm.price = v }, label: 'Preis pro Stueck', density: 'compact' })),
                  h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.pdfImportSettlementAmountText, label: 'Betrag', density: 'compact', readonly: true })),
                  h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.pdfImportSettlementDeductionsText, label: 'Gebuehr + Steuer', density: 'compact', readonly: true })),
                  h(VCol, { cols: '12', md: '4' }, () => h(VTextField, { modelValue: ctx.pdfImportForm.date, 'onUpdate:modelValue': v => { ctx.pdfImportForm.date = v }, label: 'Datum', type: 'date', density: 'compact' })),
                  h(VCol, { cols: '12', md: '4' }, () => h(VSwitch, { modelValue: ctx.pdfImportForm.force, 'onUpdate:modelValue': v => { ctx.pdfImportForm.force = v }, label: 'Force', density: 'compact', hideDetails: true })),
                  h(VCol, { cols: '12' }, () => h(VTextField, { modelValue: ctx.pdfImportForm.description, 'onUpdate:modelValue': v => { ctx.pdfImportForm.description = v }, label: 'Beschreibung nur bei bewusstem Override', density: 'compact' }))
                ])
              ])
            ]
          })
          : null
        const fields = h('div', { class: 'pdf-import-sections' }, [
          taxOptimizationSection,
          createStockSection,
          transactionSummarySection,
          confirmStockSection
        ])

        return h('div', [
          ctx.pdfImportResult?.message ? h(VAlert, { type: ctx.pdfImportHasValidationError ? 'error' : (ctx.pdfImportResult?.missing_fields?.length ? 'warning' : 'info'), variant: 'tonal', class: 'mb-3' }, () => ctx.pdfImportResult.message) : null,
          ctx.pdfImportHasValidationError ? h(VAlert, { type: 'error', variant: 'tonal', class: 'mb-3' }, () => [h('div', { class: 'font-weight-bold mb-1' }, 'Transaktion wurde nicht gebucht.'), ...ctx.pdfImportValidationErrorLines.map(line => h('div', { key: line }, line))]) : null,
          ctx.pdfImportResult?.duplicate ? h(VAlert, { type: 'warning', variant: 'tonal', class: 'mb-3' }, () => ctx.pdfImportDuplicateText) : null,
          needsNewStock ? null : submitAction,
          fields,
          h('div', { class: 'pdf-import-detail-actions mt-2' }, [
            h(VBtn, { size: 'small', variant: 'outlined', prependIcon: 'mdi-text-recognition', onClick: () => { ctx.showPdfImportOcr = !ctx.showPdfImportOcr } }, () => ctx.showPdfImportOcr ? 'OCR ausblenden' : 'OCR erkannt'),
            h(VBtn, { size: 'small', variant: 'outlined', prependIcon: 'mdi-code-json', onClick: () => { ctx.showPdfImportRaw = !ctx.showPdfImportRaw } }, () => ctx.showPdfImportRaw ? 'Response ausblenden' : 'Details Response')
          ]),
          h(VExpandTransition, null, () => ctx.showPdfImportOcr ? h(VCard, { variant: 'tonal', class: 'pdf-import-section mt-3' }, () => [
            h(VCardTitle, { class: 'text-subtitle-1' }, () => 'OCR erkannt'),
            h(VCardText, null, () => h('div', { class: 'pdf-import-kv' }, [
              h('span', 'Wertpapier'), h('strong', ctx.pdfImportPreview.stock_name || '-'),
              h('span', 'ISIN'), h('strong', ctx.pdfImportPreview.isin || '-'),
              h('span', 'Typ'), h('strong', ctx.formatPdfCategory(ctx.pdfImportPreview.stocks_transactions_category)),
              h('span', 'Menge'), h('strong', String(ctx.pdfImportPreview.stocks_menge ?? '-')),
              h('span', 'Preis'), h('strong', String(ctx.pdfImportPreview.stocks_transactions_preisprostueck ?? '-')),
              h('span', 'Datum'), h('strong', ctx.formatPdfDateTime(ctx.pdfImportPreview.stocks_transactions_datum))
            ]))
          ]) : null),
          h(VExpandTransition, null, () => ctx.showPdfImportRaw ? h('pre', { class: 'pdf-import-json mt-3' }, JSON.stringify(ctx.pdfImportResult, null, 2)) : null)
        ])
      }
    }
  },

  provide() {
    return {
      pdfImport: this
    }
  },

  props: {
    mode: {
      type: String,
      default: 'dialog',
      validator: value => ['dialog', 'inline'].includes(value)
    },
    file: {
      type: Object,
      default: null
    },
    autoAnalyze: {
      type: Boolean,
      default: false
    }
  },

  emits: ['imported', 'success', 'error'],

  data() {
    return {
      uploadDialog: false,
      resultDialog: false,
      loading: false,
      error: '',
      successMessage: '',
      successDetails: [],
      pdfImportPostImportNotice: null,
      pdfImportWarnings: [],
      pdfFile: null,
      pdfImportResult: null,
      showPdfImportRaw: false,
      showPdfImportOcr: false,
      pdfImportCreateStockLoading: false,
      pdfImportCreateStockMessage: '',
      pdfImportCreateStockMessageType: 'success',
      pdfImportCreateStockForm: {
        name: '',
        isin: '',
        typ: 'Aktie',
        handelsplatz: '',
        googleId: '',
        land: ''
      },
      pdfImportSubmittingFinal: false,
      pdfImportForm: {
        stocks_idstocks: null,
        category: 0,
        amount: '',
        price: '',
        fee: '',
        date: '',
        isin: '',
        description: '',
        force: false
      },
      stockTypeOptions: STOCK_TYPE_OPTIONS,
      pdfImportCategoryOptions: PDF_TRANSACTION_CATEGORY_OPTIONS,
      laender: LAENDER
    }
  },

  computed: {
    ...mapGetters({
      stocks: 'stateStocks',
      users: 'stateUser'
    }),

    stockSelectItems() {
      const items = (this.stocks || [])
        .filter(stock => this.getPdfImportStockId(stock) > 0)
        .filter(stock => this.isPdfImportStockOwnedByCurrentUser(stock))
        .map(stock => ({
          value: this.getPdfImportStockId(stock),
          title: this.formatPdfImportStockSelectTitle(stock)
        }))
        .sort((a, b) => a.title.localeCompare(b.title))

      this.pdfImportPreviewStockCandidates.forEach(candidate => {
        const candidateId = this.getPdfImportStockId(candidate)
        if (candidateId > 0 && !items.some(item => Number(item.value) === candidateId)) {
          items.push({
            value: candidateId,
            title: this.formatPdfImportStockSelectTitle(candidate)
          })
        }
      })

      items.sort((a, b) => a.title.localeCompare(b.title))

      return items
    },

    pdfImportPreview() {
      return this.pdfImportResult?.preview || {}
    },

    pdfImportSettlement() {
      return this.pdfImportPreview?.settlement || {}
    },

    pdfImportSettlementAmountText() {
      const settlement = this.pdfImportSettlement
      let value = settlement.actual_cash_amount_abs
      if (value === null || value === undefined || value === '') {
        const cashAmount = Number(settlement.actual_cash_amount)
        value = Number.isFinite(cashAmount) ? Math.abs(cashAmount) : settlement.position_amount
      }
      return this.formatPdfMoney(value)
    },

    pdfImportSettlementDeductionsText() {
      const settlement = this.pdfImportSettlement
      let value = settlement.deductions_total
      if (value === null || value === undefined || value === '') {
        const costs = Number(settlement.costs_total)
        const taxes = Number(settlement.taxes_total)
        value = Number.isFinite(costs) || Number.isFinite(taxes)
          ? (Number.isFinite(costs) ? costs : 0) + (Number.isFinite(taxes) ? taxes : 0)
          : null
      }
      return this.formatPdfMoney(value)
    },

    isPdfImportTaxOptimization() {
      return this.pdfImportPreview?.is_tax_refund === true
    },

    pdfImportTaxAmount() {
      const settlement = this.pdfImportSettlement
      const cashAmount = Number(settlement.actual_cash_amount)
      if (Number.isFinite(cashAmount)) return cashAmount

      const price = Number(this.pdfImportPreview?.stocks_transactions_preisprostueck)
      if (Number.isFinite(price)) return price

      const positionAmount = Number(settlement.position_amount)
      return Number.isFinite(positionAmount) ? positionAmount : null
    },

    pdfImportTaxDescriptionText() {
      return this.pdfImportPreview?.stocks_transactions_beschreibung ||
        this.pdfImportForm.description ||
        'steuer_TR_automatisch_01'
    },

    pdfImportTaxCashStockText() {
      const stockMatch = this.pdfImportPreview?.stock_match || this.selectedPdfImportStock
      const stockName = this.getPdfImportStockName(stockMatch)
      const stockId = this.getPdfImportStockId(stockMatch)
      if (stockName || stockId) return `${stockName || '-'} (#${stockId || '-'})`
      return '-'
    },

    pdfImportTaxCashImpactText() {
      const stockName = this.getPdfImportStockName(this.pdfImportPreview?.stock_match || this.selectedPdfImportStock) || 'Cash'
      return `${stockName} wird um ${this.formatPdfSignedMoney(this.pdfImportTaxAmount)} erhoeht`
    },

    pdfImportTaxDetailRows() {
      const primaryTaxLabels = new Set([
        'kapitalertragsteuer',
        'kirchensteuer',
        'solidaritätszuschlag',
        'solidaritaetszuschlag'
      ])

      return (this.pdfImportSettlement?.taxes || [])
        .filter(item => primaryTaxLabels.has(String(item?.label || '').trim().toLowerCase()))
        .map(item => ({
          label: item.label,
          amount: item.amount
        }))
    },

    pdfImportMissingFields() {
      return Array.isArray(this.pdfImportResult?.missing_fields)
        ? this.pdfImportResult.missing_fields
        : []
    },

    pdfImportMissingFieldsText() {
      return this.pdfImportMissingFields.join(', ')
    },

    selectedPdfImportStock() {
      const id = Number(this.pdfImportForm.stocks_idstocks || 0)
      const stock = (this.stocks || []).find(stock =>
        this.getPdfImportStockId(stock) === id &&
        this.isPdfImportStockOwnedByCurrentUser(stock)
      ) || null
      if (stock) return stock

      return this.pdfImportPreviewStockCandidates.find(candidate =>
        this.getPdfImportStockId(candidate) === id
      ) || null
    },

    selectedPdfImportStockText() {
      const stock = this.selectedPdfImportStock
      if (!stock) return '-'
      const statusText = this.pdfImportStockReactivation?.will_reactivate
        ? ', Status: inaktiv - wird beim Import reaktiviert'
        : ''
      return `${this.getPdfImportStockName(stock) || '-'} (#${this.getPdfImportStockId(stock) || '-'})${statusText}`
    },

    selectedPdfImportStockQuantityText() {
      const stock = this.selectedPdfImportStock
      if (!stock) return '-'
      return String(stock.stocks_Menge ?? '-')
    },

    pdfImportSellQuantityExceedsStock() {
      if (Number(this.pdfImportForm.category || 0) !== 2 || !this.selectedPdfImportStock) return false
      const sellQuantity = this.normalizePdfImportNumber(this.pdfImportForm.amount)
      const currentQuantity = this.normalizePdfImportNumber(this.selectedPdfImportStock.stocks_Menge)
      return sellQuantity > currentQuantity
    },

    hasConfirmedPdfImportStock() {
      return Number(this.pdfImportForm.stocks_idstocks || 0) > 0
    },

    showPdfImportCreateStockSection() {
      if (this.isPdfImportTaxOptimization) return false
      if (this.hasConfirmedPdfImportStock) return false

      const shouldOfferCreateStock = this.shouldOfferPdfImportCreateStock(this.pdfImportPreview)
      const stockMatch = shouldOfferCreateStock ? null : this.findPdfImportBestStockMatch(this.pdfImportPreview)
      if (this.getPdfImportStockId(stockMatch) > 0) return false

      if (this.isPdfImportSameIsinStock(this.pdfImportPreview?.stock_suggestion, this.pdfImportPreview?.isin)) return false

      const detectedName = this.firstPdfImportStockName(
        this.pdfImportPreview?.stock_name,
        this.pdfImportPreview?.create_stock_prompt?.detected_name,
        this.pdfImportPreview?.create_stock_prompt?.suggested_stock?.stocks_name
      )
      const detectedIsin = this.firstRealPdfImportIsin(
        this.pdfImportPreview?.isin,
        this.pdfImportPreview?.create_stock_prompt?.detected_isin,
        this.getPdfImportStockIsin(this.pdfImportPreview?.create_stock_prompt?.suggested_stock)
      )
      return Boolean(this.pdfImportCreateStockPrompt || detectedName || detectedIsin)
    },

    pdfImportPreviewStockCandidates() {
      return [
        this.pdfImportPreview?.stock_match,
        this.pdfImportPreview?.stock_suggestion,
        this.pdfImportPreview?.create_stock_prompt?.suggested_stock
      ].filter(candidate => this.getPdfImportStockId(candidate) > 0)
    },

    pdfImportPreviewStockCandidate() {
      return this.pdfImportPreviewStockCandidates[0] || null
    },

    pdfImportCreateStockPrompt() {
      return this.pdfImportPreview?.create_stock_prompt || null
    },

    pdfImportSuggestedStock() {
      return this.pdfImportCreateStockPrompt?.suggested_stock || {}
    },

    pdfImportCreateStockPromptMessage() {
      return this.pdfImportCreateStockPrompt?.message ||
        'Falls der Stock noch nicht in der DB vorhanden ist, hier aus den OCR-Daten neu anlegen und direkt importieren.'
    },

    pdfImportStockConfirmPlaceholder() {
      return this.pdfImportPreview.stock_name || 'Stock aus DB waehlen oder neu anlegen'
    },

    pdfImportStockReactivation() {
      return this.pdfImportPreview?.stock_reactivation || null
    },

    pdfImportStockReactivationText() {
      const reactivation = this.pdfImportStockReactivation
      if (!reactivation?.will_reactivate) return ''

      const stockName = reactivation.stocks_name || this.selectedPdfImportStock?.stocks_name || 'DB Stock'
      const stockId = reactivation.idstocks || this.selectedPdfImportStock?.idstocks || this.pdfImportForm.stocks_idstocks
      return `DB Stock: ${stockName} (#${stockId}), Status: inaktiv - wird beim Import reaktiviert.`
    },

    pdfImportDuplicateText() {
      const filename = this.pdfImportResult?.filename || 'Diese PDF'
      return `${filename} wurde vermutlich bereits gelesen und als Transaktion in der DB gespeichert. Trotzdem nochmal speichern? Dann Force aktivieren und importieren.`
    },

    pdfImportValidationErrors() {
      const errors = this.pdfImportResult?.validation_errors
      if (Array.isArray(errors)) return errors
      if (errors && typeof errors === 'object') return [errors]
      return []
    },

    pdfImportHasValidationError() {
      return Boolean(this.pdfImportResult?.Fehler) ||
        this.pdfImportValidationErrors.length > 0 ||
        String(this.pdfImportResult?.message || '').toLowerCase().includes('nicht gebucht')
    },

    pdfImportForcedDuplicateWasBooked() {
      return Boolean(this.pdfImportForm.force) &&
        this.pdfImportResult?.dry_run === false &&
        Boolean(this.pdfImportResult?.duplicate) &&
        Boolean(this.pdfImportResult?.result?.transaction?.idstocks_transactions)
    },

    pdfImportNeedsUserAttention() {
      const unresolvedDuplicate = Boolean(this.pdfImportResult?.duplicate) &&
        !this.pdfImportForcedDuplicateWasBooked

      return this.pdfImportHasValidationError ||
        this.pdfImportMissingFields.length > 0 ||
        unresolvedDuplicate
    },

    effectivePdfImportPostImportNotice() {
      return this.pdfImportPostImportNotice || this.buildPdfImportPostImportNotice(this.pdfImportResult)
    },

    pdfImportWarningLines() {
      return this.pdfImportWarnings
        .map(warning => {
          if (typeof warning === 'string') return warning.trim()
          if (!warning || typeof warning !== 'object') return ''
          return String(warning.message || warning.detail || warning.code || '').trim()
        })
        .filter(Boolean)
    },

    pdfImportValidationErrorLines() {
      const lines = []
      if (this.pdfImportResult?.Fehler) lines.push(`Fehler: ${this.pdfImportResult.Fehler}`)

      this.pdfImportValidationErrors.forEach((error, index) => {
        if (typeof error === 'string') {
          lines.push(error)
          return
        }

        const label = error.field || error.type || `Validierung ${index + 1}`
        const parts = []
        if (error.message) parts.push(error.message)
        if (error.bestand !== undefined) parts.push(`Bestand: ${error.bestand}`)
        if (error.verkaufsmenge !== undefined) parts.push(`Verkaufsmenge: ${error.verkaufsmenge}`)
        if (error.zielbestand !== undefined) parts.push(`Zielbestand: ${error.zielbestand}`)
        if (error.current_quantity !== undefined) parts.push(`Bestand: ${error.current_quantity}`)
        if (error.sell_quantity !== undefined) parts.push(`Verkaufsmenge: ${error.sell_quantity}`)
        if (error.target_quantity !== undefined) parts.push(`Zielbestand: ${error.target_quantity}`)
        lines.push(`${label}: ${parts.length ? parts.join(' | ') : JSON.stringify(error)}`)
      })

      return lines.length ? lines : ['Die API hat den PDF-Inhalt gelesen, aber die Transaktion nicht gespeichert.']
    },

    canCreatePdfImportStock() {
      return this.canSubmitPdfImportAutoStock && !this.pdfImportCreateStockLoading
    },

    showManualPdfImportStockCreation() {
      const autoStock = this.pdfImportPreview?.auto_stock || this.pdfImportResult?.auto_stock
      return this.showPdfImportCreateStockSection &&
        String(autoStock?.symbol?.status || '').toLowerCase() !== 'ok'
    },

    canCreateManualPdfImportStock() {
      const form = this.pdfImportCreateStockForm
      return this.showManualPdfImportStockCreation && Boolean(this.pdfFile) &&
        Number(this.users?.iduser) > 0 &&
        !this.loading && !this.pdfImportSubmittingFinal && !this.pdfImportCreateStockLoading &&
        this.isRealIsin(form.isin) && this.stockTypeOptions.includes(form.typ) &&
        ['name', 'handelsplatz', 'googleId', 'land'].every(key => String(form[key] || '').trim())
    },

    canSubmitPdfImport() {
      return Boolean(this.pdfFile) && this.hasConfirmedPdfImportStock && !this.loading
    },

    canSubmitPdfImportAutoStock() {
      const autoStock = this.pdfImportPreview?.auto_stock || this.pdfImportResult?.auto_stock || null
      const symbol = autoStock?.symbol || null
      return Boolean(this.pdfFile) &&
        !this.hasConfirmedPdfImportStock &&
        autoStock?.enabled === true &&
        String(symbol?.status || '').toLowerCase() === 'ok' &&
        !this.loading
    },

    showInlineActions() {
      return this.mode === 'inline' && !this.successMessage
    }
  },

  watch: {
    file: {
      immediate: true,
      async handler(file) {
        if (!file) return
        this.pdfFile = file
        if (this.autoAnalyze) {
          await this.submitDryRun()
        }
      }
    }
  },

  methods: {
    ...mapActions(['createStock', 'updateStock']),

    open(file = null) {
      this.reset()
      this.pdfFile = file
      this.uploadDialog = true
    },

    reset() {
      this.error = ''
      this.successMessage = ''
      this.pdfImportPostImportNotice = null
      this.pdfImportWarnings = []
      this.pdfFile = null
      this.pdfImportResult = null
      this.showPdfImportRaw = false
      this.showPdfImportOcr = false
      this.pdfImportCreateStockLoading = false
      this.pdfImportCreateStockMessage = ''
      this.pdfImportCreateStockMessageType = 'success'
      this.pdfImportSubmittingFinal = false
      this.pdfImportCreateStockForm = { name: '', isin: '', typ: 'Aktie', handelsplatz: '', googleId: '', land: '' }
      this.pdfImportForm = { stocks_idstocks: null, category: 0, amount: '', price: '', fee: '', date: '', isin: '', description: '', force: false }
    },

    closeUploadDialog() {
      this.uploadDialog = false
      this.error = ''
      this.pdfFile = null
    },

    async ensurePdfImportStocksLoaded() {
      try {
        await this.$store.dispatch('getStocks')
      } catch (error) {
        console.error('getStocks failed before PDF import matching', error)
      }
    },

    onFileSelected(e) {
      const files = e?.target?.files || e?.dataTransfer?.files || e?.files || e
      const file = Array.isArray(files) ? files[0] : files?.[0]
      this.pdfFile = file || null
      this.error = ''
    },

    normalizePdfImportStockSelection(value) {
      if (value && typeof value === 'object') {
        return value.value ?? value.idstocks ?? value.stocks_idstocks ?? value.id ?? value
      }

      const numericValue = Number(value)
      return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : value
    },

    formatPdfImportStockSelectTitle(stock) {
      const id = this.getPdfImportStockId(stock)
      const name = this.getPdfImportStockName(stock)
      return `${name || '-'} (#${id || '-'})`
    },

    getPdfImportStockId(stock) {
      return Number(stock?.idstocks ?? stock?.stocks_idstocks ?? stock?.stock_id ?? stock?.id ?? stock?.value ?? 0)
    },

    getPdfImportStockName(stock) {
      return stock?.stocks_name ?? stock?.stock_name ?? stock?.name ?? stock?.title ?? ''
    },

    normalizePdfImportIsin(value) {
      return String(value || '').trim().replace(/\s+/g, '').toUpperCase()
    },

    getPdfImportStockIsin(stock) {
      return this.normalizePdfImportIsin(
        stock?.stocks_ISIN ??
        stock?.stocks_isin ??
        stock?.stock_ISIN ??
        stock?.stock_isin ??
        stock?.ISIN ??
        stock?.isin ??
        stock?.stock?.stocks_ISIN ??
        stock?.stock?.isin ??
        ''
      )
    },

    getPdfImportStockHandelsplatz(stock) {
      return String(
        stock?.stocks_Handelsplatz ??
        stock?.stocks_handelsplatz ??
        stock?.handelsplatz ??
        stock?.exchange ??
        stock?.market ??
        stock?.boerse ??
        stock?.onvista?.stocks_Handelsplatz ??
        stock?.onvista?.handelsplatz ??
        stock?.onvista?.exchange ??
        ''
      ).trim()
    },

    getPdfImportStockGoogleId(stock) {
      return String(
        stock?.stocks_GoogleId ??
        stock?.stocks_google_id ??
        stock?.stocks_googleid ??
        stock?.googleId ??
        stock?.google_id ??
        stock?.google_symbol ??
        stock?.symbol ??
        stock?.ticker ??
        stock?.onvista?.stocks_GoogleId ??
        stock?.onvista?.googleId ??
        stock?.onvista?.google_id ??
        stock?.onvista?.symbol ??
        ''
      ).trim()
    },

    getPdfImportAutoStockSymbol(source) {
      return source?.auto_stock?.symbol || source?.symbol || null
    },

    getPdfImportAutoStockGoogleId(source) {
      const symbol = this.getPdfImportAutoStockSymbol(source)
      return String(
        symbol?.symbol ??
        symbol?.googleId ??
        symbol?.google_id ??
        symbol?.google_symbol ??
        ''
      ).trim()
    },

    getPdfImportAutoStockHandelsplatz(source) {
      const symbol = this.getPdfImportAutoStockSymbol(source)
      const explicitExchange = String(
        symbol?.stocks_Handelsplatz ??
        symbol?.handelsplatz ??
        symbol?.exchange ??
        symbol?.market ??
        symbol?.boerse ??
        ''
      ).trim()
      if (explicitExchange) return explicitExchange

      const googleId = this.getPdfImportAutoStockGoogleId(source)
      const sourceName = String(symbol?.source || '').trim().toLowerCase()
      if (sourceName === 'onvista' && /\d/.test(googleId)) return 'FRA'

      return ''
    },

    normalizePdfImportNumber(value) {
      const text = String(value ?? '').trim().replace(',', '.')
      const numberValue = Number(text)
      return Number.isFinite(numberValue) ? numberValue : 0
    },

    normalizePdfImportDate(value) {
      const text = String(value || '').trim()
      if (!text) return ''
      return text.slice(0, 10)
    },

    appendPdfImportFormData(dryRun) {
      const formData = new FormData()
      formData.append('file', this.pdfFile)
      formData.append('dry_run', dryRun ? 'true' : 'false')
      if (dryRun) return formData

      formData.append('force', this.pdfImportForm.force ? 'true' : 'false')
      if (!this.hasConfirmedPdfImportStock) return formData

      formData.append('stocks_idstocks', String(Number(this.pdfImportForm.stocks_idstocks)))
      formData.append('category', String(Number(this.pdfImportForm.category || 0)))
      formData.append('menge', String(this.normalizePdfImportNumber(this.pdfImportForm.amount)))
      formData.append('preis', String(this.normalizePdfImportNumber(this.pdfImportForm.price)))
      formData.append('gebuehr', String(this.normalizePdfImportNumber(this.pdfImportForm.fee)))
      formData.append('datum', this.pdfImportForm.date || 'string')
      if (String(this.pdfImportForm.description || '').trim()) {
        formData.append('beschreibung', this.pdfImportForm.description.trim())
      }
      return formData
    },

    applyPdfImportPreview(result) {
      const preview = result?.preview || {}
      const createPrompt = preview.create_stock_prompt || {}
      const suggestedStock = createPrompt.suggested_stock || {}
      const autoStock = preview.auto_stock || result?.auto_stock || null
      const shouldOfferCreateStock = this.shouldOfferPdfImportCreateStock(preview)
      const matchedStock = shouldOfferCreateStock ? null : this.findPdfImportBestStockMatch(preview)
      const matchedId = this.getPdfImportStockId(matchedStock)
      const currentStockId = Number(this.pdfImportForm.stocks_idstocks || 0)
      const previewName = this.firstPdfImportStockName(
        preview.stock_name,
        createPrompt.detected_name,
        suggestedStock.input_name,
        suggestedStock.stocks_name,
        this.getPdfImportStockName(matchedStock)
      )
      const previewIsin = this.firstRealPdfImportIsin(
        preview.isin,
        createPrompt.detected_isin,
        this.getPdfImportStockIsin(suggestedStock),
        this.getPdfImportStockIsin(matchedStock)
      )
      const confirmedStockId = currentStockId > 0
        ? currentStockId
        : (matchedId > 0 ? matchedId : null)

      this.pdfImportForm = {
        ...this.pdfImportForm,
        stocks_idstocks: confirmedStockId || null,
        category: Number(preview.stocks_transactions_category ?? this.pdfImportForm.category ?? 0),
        amount: preview.stocks_menge ?? this.pdfImportForm.amount,
        price: preview.stocks_transactions_preisprostueck ?? this.pdfImportForm.price,
        fee: preview.stocks_gebuehr ?? this.pdfImportForm.fee,
        date: this.normalizePdfImportDate(preview.stocks_transactions_datum) || this.pdfImportForm.date,
        isin: previewIsin || this.pdfImportForm.isin
      }

      this.pdfImportCreateStockForm = {
        ...this.pdfImportCreateStockForm,
        name: this.pdfImportCreateStockForm.name || previewName,
        isin: this.pdfImportCreateStockForm.isin || previewIsin,
        typ: this.pdfImportCreateStockForm.typ || suggestedStock.stocks_Typ || 'Aktie',
        handelsplatz: this.pdfImportCreateStockForm.handelsplatz ||
          this.getPdfImportStockHandelsplatz(suggestedStock) ||
          this.getPdfImportAutoStockHandelsplatz(autoStock),
        googleId: this.pdfImportCreateStockForm.googleId ||
          this.getPdfImportStockGoogleId(suggestedStock) ||
          this.getPdfImportAutoStockGoogleId(autoStock),
        land: this.pdfImportCreateStockForm.land || suggestedStock.stocks_Land || this.inferPdfImportLandFromIsin(previewIsin)
      }
    },

    inferPdfImportLandFromIsin(isin) {
      const prefix = String(isin || '').trim().toUpperCase().slice(0, 2)
      const landByPrefix = { DE: 'Deutschland', US: 'USA', CA: 'Kanada', CH: 'Schweiz', FR: 'Frankreich', IT: 'Italien', NL: 'Niederlande', DK: 'Daenemark', NO: 'Norwegen', SE: 'Schweden', JP: 'Japan', CN: 'China', AU: 'Australien', KR: 'Suedkorea', TW: 'Taiwan' }
      return landByPrefix[prefix] || ''
    },

    isRealIsin(value) {
      const text = this.normalizePdfImportIsin(value)
      return /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(text)
    },

    firstRealPdfImportIsin(...values) {
      const normalizedValues = values.map(value => this.normalizePdfImportIsin(value)).filter(Boolean)
      const realValue = normalizedValues.find(value => this.isRealIsin(value))
      return realValue || normalizedValues[0] || ''
    },

    firstPdfImportStockName(...values) {
      return String(values.find(value => String(value || '').trim()) || '').trim()
    },

    findStockById(id) {
      const numericId = Number(id)
      return (this.stocks || []).find(stock => this.getPdfImportStockId(stock) === numericId && this.isPdfImportStockOwnedByCurrentUser(stock))
    },

    getPdfImportStockUserId(stock) {
      return Number(stock?.user_iduser?.iduser ?? stock?.user_iduser_id ?? stock?.user_iduser ?? stock?.user_id ?? 0)
    },

    isPdfImportStockOwnedByCurrentUser(stock) {
      const currentUserId = Number(this.users?.iduser || 0)
      const stockUserId = this.getPdfImportStockUserId(stock)
      if (!currentUserId) return true
      if (!stockUserId) return true
      return stockUserId === currentUserId
    },

    normalizePdfImportStockName(value) {
      return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
    },

    normalizePdfImportStockMatchName(value) {
      return this.normalizePdfImportStockName(value)
        .replace(/\s*\(([a-z0-9]{1,4})\)\s*$/i, '')
        .replace(/\s+(class|cl|klasse)\s+[a-z0-9]{1,4}$/i, '')
        .replace(/[^a-z0-9]+/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },

    isPdfImportSameStockName(a, b) {
      const first = this.normalizePdfImportStockName(a)
      const second = this.normalizePdfImportStockName(b)
      if (!first || !second) return false
      if (first === second) return true

      const firstMatch = this.normalizePdfImportStockMatchName(a)
      const secondMatch = this.normalizePdfImportStockMatchName(b)
      return Boolean(firstMatch && secondMatch && firstMatch === secondMatch)
    },

    getPdfImportPreviewStockNames(preview) {
      const createPrompt = preview?.create_stock_prompt || {}
      const suggestedStock = createPrompt.suggested_stock || {}
      return [
        preview?.stock_name,
        preview?.detected_name,
        createPrompt.detected_name,
        suggestedStock.input_name,
        this.getPdfImportStockName(suggestedStock),
        this.getPdfImportStockName(preview?.stock_match),
        this.getPdfImportStockName(preview?.stock_suggestion)
      ].filter(value => String(value || '').trim())
    },

    shouldOfferPdfImportCreateStock(preview) {
      const createPrompt = preview?.create_stock_prompt || null
      if (!createPrompt) return false

      const apiMatch = preview?.stock_match || null
      if (this.getPdfImportStockId(apiMatch) > 0) return false

      const exactLocalMatch = this.findPdfImportLocalStockMatch(preview, { allowNameMatch: false })
      if (this.getPdfImportStockId(exactLocalMatch) > 0) return false

      const detectedName = this.firstPdfImportStockName(
        preview?.stock_name,
        createPrompt.detected_name,
        createPrompt.suggested_stock?.input_name,
        createPrompt.suggested_stock?.stocks_name
      )
      const detectedIsin = this.firstRealPdfImportIsin(
        preview?.isin,
        createPrompt.detected_isin,
        this.getPdfImportStockIsin(createPrompt.suggested_stock)
      )

      return Boolean(detectedName || detectedIsin || createPrompt.message)
    },

    findPdfImportBestStockMatch(preview) {
      if (this.shouldOfferPdfImportCreateStock(preview)) return null

      const apiMatch = preview?.stock_match || null
      if (this.getPdfImportStockId(apiMatch) > 0) return apiMatch

      const localMatch = this.findPdfImportLocalStockMatch(preview, { allowNameMatch: true })
      if (this.getPdfImportStockId(localMatch) > 0) return localMatch

      const apiSuggestion = preview?.stock_suggestion || null
      if (this.isPdfImportSameIsinStock(apiSuggestion, preview?.isin)) return apiSuggestion

      const createPromptSuggestion = preview?.create_stock_prompt?.suggested_stock || null
      if (this.isPdfImportSameIsinStock(createPromptSuggestion, preview?.isin)) return createPromptSuggestion

      return null
    },

    findPdfImportLocalStockMatch(preview, options = {}) {
      const allowNameMatch = options.allowNameMatch !== false
      const previewIsin = this.firstRealPdfImportIsin(
        preview?.isin,
        preview?.detected_isin,
        preview?.create_stock_prompt?.detected_isin,
        preview?.create_stock_prompt?.suggested_stock?.stocks_ISIN,
        this.getPdfImportStockIsin(preview?.stock_match),
        this.getPdfImportStockIsin(preview?.stock_suggestion)
      )
      if (this.isRealIsin(previewIsin)) {
        const byIsin = (this.stocks || []).find(stock => this.isPdfImportStockOwnedByCurrentUser(stock) && this.getPdfImportStockIsin(stock) === previewIsin)
        if (byIsin) return byIsin
      }
      if (!allowNameMatch) return null
      const previewNames = this.getPdfImportPreviewStockNames(preview)
      if (!previewNames.length) return null
      return (this.stocks || []).find(stock =>
        this.isPdfImportStockOwnedByCurrentUser(stock) &&
        previewNames.some(name => this.isPdfImportSameStockName(this.getPdfImportStockName(stock), name))
      ) || null
    },

    isPdfImportSameIsinStock(stock, isin) {
      const stockId = this.getPdfImportStockId(stock)
      if (!stockId) return false

      const previewIsin = this.normalizePdfImportIsin(isin)
      const stockIsin = this.getPdfImportStockIsin(stock)
      return this.isRealIsin(previewIsin) && stockIsin === previewIsin
    },

    findPdfImportCreatedStock(createdStock) {
      const responseId = this.getPdfImportStockId(createdStock)
      if (responseId > 0) return this.findStockById(responseId)
      const isin = this.normalizePdfImportIsin(this.pdfImportCreateStockForm.isin)
      if (isin) {
        const byIsin = (this.stocks || []).find(stock => this.isPdfImportStockOwnedByCurrentUser(stock) && this.getPdfImportStockIsin(stock) === isin)
        if (byIsin) return byIsin
      }
      const name = String(this.pdfImportCreateStockForm.name || '').trim().toLowerCase()
      return (this.stocks || []).find(stock => this.isPdfImportStockOwnedByCurrentUser(stock) && String(stock?.stocks_name || '').trim().toLowerCase() === name) || null
    },

    buildPdfImportCreateStockPayload() {
      const suggestedStock = this.pdfImportCreateStockPrompt?.suggested_stock || {}
      const autoStock = this.pdfImportPreview?.auto_stock || this.pdfImportResult?.auto_stock || null
      const isin = this.normalizePdfImportIsin(this.pdfImportCreateStockForm.isin)
      const kaufdatum = this.pdfImportForm.date ? `${this.pdfImportForm.date}T00:00:00` : new Date().toISOString()
      const kaufkurs = this.normalizePdfImportNumber(this.pdfImportForm.price)
      return {
        stocks_name: String(this.pdfImportCreateStockForm.name || '').trim(),
        stocks_Handelsplatz: String(
          this.pdfImportCreateStockForm.handelsplatz ||
          this.getPdfImportStockHandelsplatz(suggestedStock) ||
          this.getPdfImportAutoStockHandelsplatz(autoStock)
        ).trim(),
        stocks_GoogleId: String(
          this.pdfImportCreateStockForm.googleId ||
          this.getPdfImportStockGoogleId(suggestedStock) ||
          this.getPdfImportAutoStockGoogleId(autoStock)
        ).trim(),
        stocks_Kaufdatum: kaufdatum,
        stocks_ISIN: isin,
        stocks_Kaufkurs: kaufkurs,
        stocks_Menge: '0',
        stocks_Typ: this.pdfImportCreateStockForm.typ || 'Aktie',
        stocks_Status: 1,
        stock_Umrechnungskurs: 1,
        stock_Realsiert: 0,
        stock_Aktuellerkurs: kaufkurs,
        stocks_Land: this.pdfImportCreateStockForm.land || this.inferPdfImportLandFromIsin(isin),
        stocks_stoploss: 0,
        stock_25ProzentVerkauflimit: 0,
        stock_delta1_prozent: 0,
        stock_delta5_prozent: 0,
        stock_delta20_prozent: 0,
        user_iduser_id: this.users?.iduser ?? 1
      }
    },

    async createStockFromItem(item) {
      const today = new Date().toISOString()
      const neuerStock = {
        stocks_name: item.stocks_name,
        stocks_Handelsplatz: item.stocks_Handelsplatz,
        stocks_GoogleId: item.stocks_GoogleId,
        stocks_Kaufdatum: item.stocks_Kaufdatum || today,
        stocks_ISIN: item.stocks_ISIN,
        stocks_Kaufkurs: item.stocks_Kaufkurs,
        stocks_Menge: String(item.stocks_Menge ?? 0),
        stocks_Typ: item.stocks_Typ ?? 'Aktie',
        stocks_Status: item.stocks_Status ?? 1,
        stock_Umrechnungskurs: item.stock_Umrechnungskurs ?? 1,
        stock_Realsiert: item.stock_Realsiert ?? 0,
        stock_Aktuellerkurs: item.stock_Aktuellerkurs ?? 0,
        stocks_Land: item.stocks_Land,
        stocks_stoploss: item.stocks_stoploss ?? 0,
        stock_25ProzentVerkauflimit: item.stock_25ProzentVerkauflimit ?? 0,
        stock_delta1_prozent: item.stock_delta1_prozent ?? 0,
        stock_delta5_prozent: item.stock_delta5_prozent ?? 0,
        stock_delta20_prozent: item.stock_delta20_prozent ?? 0,
        user_iduser_id: item.user_iduser_id ?? this.users?.iduser ?? 1
      }
      const createdStock = await this.createStock(neuerStock)
      await this.$store.dispatch('getStocks')
      return createdStock
    },

    async createManualPdfImportStock() {
      if (!this.canCreateManualPdfImportStock) return
      this.pdfImportCreateStockLoading = true
      this.loading = true
      this.error = ''
      try {
        const payload = this.buildPdfImportCreateStockPayload()
        await this.$store.dispatch('getStocks')
        const existing = (this.stocks || []).find(stock =>
          this.getPdfImportStockUserId(stock) === Number(this.users.iduser) &&
          this.getPdfImportStockIsin(stock) === payload.stocks_ISIN
        )
        // Keep the returned ID before refreshing, so a failed refresh cannot lose the new stock.
        const stock = existing || (await axios.post('savestock', payload)).data
        const id = this.getPdfImportStockId(stock)
        if (!(id > 0)) {
          this.error = 'Die Stock-Anlage hat keine Stock-ID zurückgegeben. Bitte vor erneutem Versuch den Bestand prüfen.'
          return
        }
        this.pdfImportForm.stocks_idstocks = id
        this.pdfImportForm.isin = payload.stocks_ISIN
        await this.$store.dispatch('getStocks')
        await this.submitFinal({ allowWhileLoading: true })
        if (!this.successMessage) {
          this.error = 'Stock ist ausgewählt. Die Transaktion wurde nicht bestätigt. Bitte die Importmeldung prüfen und nur die Transaktion erneut buchen.'
        }
      } catch (error) {
        this.error = this.hasConfirmedPdfImportStock
          ? 'Stock ist angelegt oder vorhanden und bleibt ausgewählt. Import fehlgeschlagen: ' + (error?.response?.data?.detail || error?.message || 'Unbekannter Fehler')
          : 'Manuelle Anlage fehlgeschlagen. Vor erneutem Versuch den Bestand prüfen: ' + (error?.response?.data?.detail || error?.message || 'Unbekannter Fehler')
      } finally {
        this.loading = false
        this.pdfImportCreateStockLoading = false
      }
    },

    async createPdfImportStockFromPreview() {
      if (!this.canCreatePdfImportStock) return
      if (this.loading || this.pdfImportSubmittingFinal) return
      this.pdfImportCreateStockLoading = true
      this.pdfImportCreateStockMessage = ''
      this.pdfImportCreateStockMessageType = 'success'
      try {
        await this.submitFinal({ autoCreateStock: true })
        if (this.pdfImportNeedsUserAttention) {
          this.pdfImportCreateStockMessageType = 'warning'
          this.pdfImportCreateStockMessage =
            this.pdfImportResult?.result?.Fehler ||
            this.pdfImportResult?.Fehler ||
            this.pdfImportResult?.auto_stock?.db?.reason ||
            'Stock wurde nicht angelegt und die PDF-Transaktion wurde nicht gebucht.'
        }
      } catch (error) {
        this.pdfImportCreateStockMessageType = 'error'
        this.pdfImportCreateStockMessage = error?.response?.data?.detail || error?.message || 'Stock konnte nicht angelegt werden.'
      } finally {
        this.pdfImportCreateStockLoading = false
      }
    },

    async updateSelectedStockMasterDataFromPdf() {
      const isin = this.normalizePdfImportIsin(this.pdfImportForm.isin)
      const createPrompt = this.pdfImportPreview?.create_stock_prompt || {}
      const suggestedStock = this.pdfImportPreview?.stock_suggestion || {}
      const selectedStock = this.selectedPdfImportStock
      const pdfStockName = this.firstPdfImportStockName(
        this.pdfImportPreview.stock_name,
        createPrompt.detected_name,
        suggestedStock.input_name,
        this.getPdfImportStockName(suggestedStock),
        this.getPdfImportStockName(selectedStock)
      )
      const stock = this.findStockById(this.pdfImportForm.stocks_idstocks)
      if (!stock) return
      const stockAfter = this.getPdfImportStockAfter(stock)
      const stockForPayload = {
        ...stock,
        ...stockAfter
      }
      const currentIsin = this.getPdfImportStockIsin(stockForPayload)
      const nextIsin = this.isRealIsin(isin) && !this.isRealIsin(currentIsin) ? isin : stockForPayload.stocks_ISIN
      const nextName = pdfStockName && pdfStockName !== String(stockForPayload.stocks_name || '').trim() ? pdfStockName : stockForPayload.stocks_name
      const nextStatus = this.pdfImportStockReactivation?.will_reactivate ? 1 : stockForPayload.stocks_Status
      if (nextIsin === stockForPayload.stocks_ISIN && nextName === stockForPayload.stocks_name && nextStatus === stockForPayload.stocks_Status) return
      await this.updateStock(this.stockUpdatePayload(stockForPayload, {
        stocks_name: nextName,
        stocks_ISIN: nextIsin,
        stocks_Status: nextStatus
      }))
    },

    getPdfImportStockAfter(stock) {
      const stockAfter = this.pdfImportResult?.stock_after || null
      if (!stockAfter || this.getPdfImportStockId(stockAfter) !== this.getPdfImportStockId(stock)) return {}
      return Object.fromEntries(
        Object.entries(stockAfter).filter(([, value]) => value !== undefined && value !== null)
      )
    },

    stockUpdatePayload(stock, overrides = {}) {
      return {
        idstocks: stock.idstocks,
        stocks_name: stock.stocks_name,
        stocks_Handelsplatz: stock.stocks_Handelsplatz,
        stocks_GoogleId: stock.stocks_GoogleId,
        stocks_Kaufdatum: stock.stocks_Kaufdatum,
        stocks_ISIN: stock.stocks_ISIN,
        stocks_Kaufkurs: stock.stocks_Kaufkurs,
        stocks_Menge: String(stock.stocks_Menge ?? 0),
        stocks_Typ: stock.stocks_Typ,
        stocks_Status: stock.stocks_Status,
        stock_Umrechnungskurs: stock.stock_Umrechnungskurs,
        stock_Realsiert: stock.stock_Realsiert,
        stock_Aktuellerkurs: parseFloat(stock.stock_Aktuellerkurs || 0),
        stocks_Land: stock.stocks_Land,
        stocks_stoploss: stock.stocks_stoploss,
        stock_25ProzentVerkauflimit: stock.stock_25ProzentVerkauflimit,
        stock_delta1_prozent: stock.stock_delta1_prozent,
        stock_delta5_prozent: stock.stock_delta5_prozent,
        stock_delta20_prozent: stock.stock_delta20_prozent,
        user_iduser_id: stock.user_iduser?.iduser ?? this.users?.iduser ?? 1,
        ...overrides
      }
    },

    async ensurePdfImportSellStockQuantityUpdated(stockBeforeImport) {
      if (Number(this.pdfImportForm.category || 0) !== 2 || !stockBeforeImport?.idstocks) return
      if (this.pdfImportNeedsUserAttention) return
      const sellQuantity = this.normalizePdfImportNumber(this.pdfImportForm.amount)
      const beforeQuantity = this.normalizePdfImportNumber(stockBeforeImport.stocks_Menge)
      const expectedQuantity = beforeQuantity - sellQuantity
      if (!Number.isFinite(expectedQuantity) || expectedQuantity < 0) return
      const currentStock = this.findStockById(stockBeforeImport.idstocks)
      if (!currentStock) return
      const currentQuantity = this.normalizePdfImportNumber(currentStock?.stocks_Menge)
      if (Math.abs(currentQuantity - expectedQuantity) < 0.000001) return
      await this.updateStock(this.stockUpdatePayload(currentStock, { stocks_Menge: String(expectedQuantity) }))
      await this.$store.dispatch('getStocks')
    },

    async submitDryRun() {
      if (!this.pdfFile) return
      if (this.loading || this.pdfImportSubmittingFinal) return
      this.loading = true
      this.error = ''
      this.successMessage = ''
      this.pdfImportPostImportNotice = null
      this.pdfImportWarnings = []
      try {
        this.pdfFile = await normalizePdfFile(this.pdfFile)
        const { data } = await axios.post('/transactions/import-pdf', this.appendPdfImportFormData(true), {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        await this.ensurePdfImportStocksLoaded()
        this.pdfImportResult = data
        this.pdfImportWarnings = Array.isArray(data?.warnings) ? data.warnings : []
        this.applyPdfImportPreview(data)
        this.uploadDialog = false
        if (this.canAutoBookPdfImportAfterPreview()) {
          this.resultDialog = false
          await this.submitFinal({ allowWhileLoading: true })
        } else {
          this.resultDialog = true
        }
      } catch (error) {
        this.error = error?.response?.data?.detail || error?.message || 'PDF Import fehlgeschlagen'
        this.pdfImportResult = error?.response?.data || null
        this.$emit('error', error)
      } finally {
        this.loading = false
      }
    },

    canAutoBookPdfImportAfterPreview() {
      return Boolean(this.pdfFile) &&
        this.hasConfirmedPdfImportStock &&
        Number(this.pdfImportForm.category || 0) > 0 &&
        !this.pdfImportNeedsUserAttention &&
        !this.pdfImportSellQuantityExceedsStock
    },

    async submitFinal(options = {}) {
      const canSubmitExistingStock = Boolean(this.pdfFile) &&
        this.hasConfirmedPdfImportStock &&
        (options.allowWhileLoading || !this.loading)

      if (!canSubmitExistingStock && !(options.autoCreateStock && this.canSubmitPdfImportAutoStock)) return
      if (this.pdfImportSubmittingFinal) return
      this.pdfImportSubmittingFinal = true
      this.loading = true
      this.error = ''
      this.successMessage = ''
      this.pdfImportPostImportNotice = null
      this.pdfImportWarnings = []
      const stockBeforeImport = this.selectedPdfImportStock ? { ...this.selectedPdfImportStock } : null
      try {
        const { data } = await axios.post('/transactions/import-pdf', this.appendPdfImportFormData(false), {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.pdfImportResult = data
        this.pdfImportWarnings = Array.isArray(data?.warnings) ? data.warnings : []
        this.applyPdfImportPreview(data)
        this.pdfImportPostImportNotice = this.buildPdfImportPostImportNotice(data)
        await this.$store.dispatch('getStocks')
        await this.updateSelectedStockMasterDataFromPdf()
        await this.ensurePdfImportSellStockQuantityUpdated(stockBeforeImport)
        if (!this.pdfImportNeedsUserAttention) {
          this.successDetails = this.buildPdfImportSuccessDetails(data)
          this.successMessage = 'PDF-Transaktion wurde importiert.'
          this.resultDialog = this.mode === 'dialog'
          this.$emit('success', data)
          this.$emit('imported', data)
          if (this.mode === 'inline') this.pdfImportResult = null
        }
      } catch (error) {
        this.pdfImportResult = error?.response?.data || { message: error?.message || 'PDF Import fehlgeschlagen' }
        this.pdfImportWarnings = Array.isArray(this.pdfImportResult?.warnings) ? this.pdfImportResult.warnings : []
        this.resultDialog = true
        this.showPdfImportRaw = true
        this.$emit('error', error)
      } finally {
        this.pdfImportSubmittingFinal = false
        this.loading = false
      }
    },

    buildPdfImportSuccessDetails(data) {
      const stockId = this.getPdfImportStockId(data?.stock_after) || Number(this.pdfImportForm.stocks_idstocks)
      // Use the refreshed holding, including any quantity correction after a sale.
      const stock = this.findStockById(stockId) || data?.stock_after
      const name = this.getPdfImportStockName(stock) || this.pdfImportPreview.stock_name || 'Nicht verfügbar'
      const formatQuantity = value => {
        if (value === null || value === undefined || value === '') return 'Nicht verfügbar'
        const quantity = this.normalizePdfImportNumber(value)
        return Number.isFinite(quantity)
          ? new Intl.NumberFormat('de-DE', { maximumFractionDigits: 12 }).format(quantity)
          : 'Nicht verfügbar'
      }
      return [
        `Stock: ${name}`,
        `Transaktion: ${this.formatPdfCategory(this.pdfImportForm.category)}`,
        `Transaktionsmenge: ${formatQuantity(this.pdfImportForm.amount)}`,
        `Abrechnungsbetrag: ${this.pdfImportSettlementAmountText}`,
        `Neue Gesamtmenge: ${formatQuantity(stock?.stocks_Menge)}`
      ]
    },

    formatPdfCategory(value) {
      const category = Number(value)
      const option = PDF_TRANSACTION_CATEGORY_OPTIONS.find(option => option.value === category)
      if (option) return option.title
      return value === null || value === undefined || value === '' ? '-' : `Unbekannt (${value})`
    },

    formatPdfDateTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString('de-DE')
    },

    formatPdfMoney(value) {
      if (value === null || value === undefined || value === '') return '-'
      const numberValue = Number(value)
      if (!Number.isFinite(numberValue)) return '-'
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numberValue)
    },

    formatPdfSignedMoney(value) {
      if (value === null || value === undefined || value === '') return '-'
      const numberValue = Number(value)
      if (!Number.isFinite(numberValue)) return '-'
      const formatted = this.formatPdfMoney(Math.abs(numberValue))
      return `${numberValue >= 0 ? '+' : '-'}${formatted}`
    },

    buildPdfImportPostImportNotice(data) {
      const autoStock = data?.auto_stock || null
      if (!autoStock) return null

      const sheet = autoStock.sheet || {}
      const autoStockStatus = String(autoStock.status || '').toLowerCase()
      const sheetStatus = String(sheet.status || '').toLowerCase()
      const sheetName = sheet.name || sheet.sheet_name || sheet.spreadsheet_name || sheet.title || ''
      const sheetCreatedLine = sheetName
        ? `In ${sheetName} wurde eine neue Zeile angelegt.`
        : 'Es wurde eine neue Google-Sheets-Zeile angelegt.'
      const lines = []
      let type = 'info'
      let title = 'Google Sheets'

      if (autoStockStatus === 'created') {
        lines.push('DB-Stock wurde automatisch neu angelegt.')
      } else if (autoStockStatus === 'skipped') {
        lines.push(autoStock.reason || 'Kein neuer DB-Stock angelegt.')
      }

      if (sheetStatus === 'created' || sheetStatus === 'appended') {
        type = 'error'
        title = 'Neuer Stock: Urladung notwendig'
        lines.push(sheetCreatedLine)
        lines.push('Bitte fuer neuen Stock Urladung durchfuehren.')
      } else if (sheetStatus === 'skipped' || sheetStatus === 'not_attempted') {
        lines.push(sheet.reason || 'Keine neue Google-Sheet-Zeile angelegt.')
        lines.push('Neu laden ist fuer Google Sheets nicht notwendig.')
      } else if (sheetStatus === 'failed' || sheetStatus === 'error') {
        type = 'warning'
        title = 'Google-Sheet-Zeile nicht angelegt'
        lines.push(...this.getPdfImportSheetErrorLines(sheet))
        lines.push(autoStock?.db?.reason || 'DB-Stock wurde nicht gespeichert.')
      } else if (autoStock.reason) {
        lines.push(autoStock.reason)
      }

      if (sheet.matched_by) {
        const matchedRow = sheet.matched_row ? `, Zeile ${sheet.matched_row}` : ''
        lines.push(`Bestehender Sheet-Eintrag erkannt ueber ${sheet.matched_by}${matchedRow}.`)
      }

      return lines.length ? { type, title, lines } : null
    },

    getPdfImportSheetErrorLines(sheet) {
      const fields = ['reason', 'error', 'message', 'detail', 'exception', 'traceback']
      const lines = fields
        .map(field => {
          const value = sheet?.[field]
          if (value === null || value === undefined || value === '') return ''
          const text = typeof value === 'string' ? value : JSON.stringify(value)
          return `${field}: ${text}`
        })
        .filter(Boolean)

      return lines.length ? lines : ['Google Sheets konnte nicht aktualisiert werden.']
    }
  }
}
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-sections) {
  display: grid;
  gap: 12px;
}

/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-section) {
  border-radius: 8px;
}

/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-kv) {
  display: grid;
  grid-template-columns: minmax(130px, 0.45fr) minmax(0, 1fr);
  gap: 6px 14px;
  align-items: baseline;
}

:deep(.pdf-import-kv span) {
  color: inherit;
  font-size: 0.82rem;
  opacity: 0.7;
}

:deep(.pdf-import-kv strong) {
  min-width: 0;
  overflow-wrap: anywhere;
}

/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-json) {
  max-height: 300px;
  overflow: auto;
  white-space: pre-wrap;
}

/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-primary-action) {
  display: flex;
  justify-content: flex-end;
}

/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-detail-actions),
/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-create-actions),
.shared-pdf-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-create-actions) {
  justify-content: flex-end;
}

/*noinspection CssUnusedSymbol*/
:deep(.pdf-import-create-message) {
  flex: 1 1 260px;
}

.shared-pdf-action-btn {
  min-width: 0;
}

@media (max-width: 600px) {
  /*noinspection CssUnusedSymbol*/
  :deep(.pdf-import-kv) {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  /*noinspection CssUnusedSymbol*/
  :deep(.pdf-import-create-actions) {
    justify-content: stretch;
  }

  /*noinspection CssUnusedSymbol*/
  :deep(.pdf-import-primary-action) {
    justify-content: stretch;
  }

  /*noinspection CssUnusedSymbol*/
  :deep(.pdf-import-primary-action-btn) {
    width: 100%;
  }

  .shared-pdf-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .shared-pdf-action-spacer {
    display: none;
  }

  .shared-pdf-action-btn {
    width: 100%;
  }
}
</style>
